import { useEffect } from 'react'
import { useParams } from 'react-router'
import { useShallow } from 'zustand/react/shallow'

import { Player, PlayerGroup } from '@img-arena/consoles-api-golf-pricing'
import { Autocomplete } from '@/components/inputs/Autocomplete/Autocomplete'
import { usePlayerGroupsStore } from '@/stores/golfPricing/playerGroups.store'

import { MarketTypes } from '../constants'

type PlayerOption = Player | PlayerGroup

type PlayersSelectorProps = {
  marketType: string | null
  selectedPlayerIds: number[]
  onChange: (playerIds: number[]) => void
}

export const PlayersSelector = ({
  marketType,
  selectedPlayerIds,
  onChange
}: PlayersSelectorProps) => {
  const { tournamentId } = useParams<{ tournamentId: string }>()

  const { data: playerGroups, fetchData: fetchPlayerGroups } = usePlayerGroupsStore(
    useShallow((state) => ({
      data: state.data,
      fetchData: state.fetchData,
      resetState: state.resetState
    }))
  )

  const playerOptions: PlayerOption[] =
    marketType === MarketTypes.WINNER
      ? playerGroups?.flatMap((group) => group.players) || []
      : playerGroups?.filter((group) => group.players.length === 3) || []

  useEffect(() => {
    if (marketType && tournamentId) {
      fetchPlayerGroups({
        competitionId: Number(tournamentId),
        marketType: marketType
      })
    }
  }, [marketType, tournamentId, fetchPlayerGroups])

  const handlePlayersChange = (players: PlayerOption[]) => {
    const playerIds = players.flatMap((p) => {
      if ('playerId' in p) {
        return [p.playerId]
      } else {
        return p.players?.map((player) => player.playerId) || []
      }
    })
    onChange(playerIds)
  }

  if (!marketType) {
    return null
  }

  if (marketType === MarketTypes.WINNER) {
    return (
      <Autocomplete<Player>
        label="Players"
        options={playerOptions.filter((p): p is Player => 'playerId' in p)}
        value={selectedPlayerIds
          .map((id) => playerOptions.find((p) => 'playerId' in p && p.playerId === id))
          .filter((p): p is Player => p !== undefined)}
        onChange={(value: Player[]) => {
          handlePlayersChange(value)
        }}
        getOptionLabel={(option: Player) => option.playerFullName}
        filterOptions={(options, { inputValue }) =>
          options.filter((option) =>
            option.playerFullName.toLowerCase().includes(inputValue.toLowerCase())
          )
        }
        disabled={!marketType}
        multiselect={true}
        required
      />
    )
  }

  return (
    <Autocomplete<PlayerGroup>
      label="Players"
      options={playerOptions.filter((p): p is PlayerGroup => 'players' in p)}
      value={
        selectedPlayerIds.length > 0
          ? (playerOptions.find(
              (p) =>
                'players' in p &&
                p.players?.every((player) => selectedPlayerIds.includes(player.playerId))
            ) as PlayerGroup | undefined)
          : null
      }
      onChange={(value: PlayerGroup | null) => {
        handlePlayersChange(value ? [value] : [])
      }}
      getOptionLabel={(option: PlayerGroup) =>
        option.players?.map((p) => p.playerFullName).join(', ') || ''
      }
      filterOptions={(options, { inputValue }) =>
        options.filter((option) =>
          option.players?.some((player) =>
            player.playerFullName.toLowerCase().includes(inputValue.toLowerCase())
          )
        )
      }
      disabled={!marketType}
      multiselect={false}
      required
    />
  )
}
